import React, { useMemo, useState } from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import { inputVariants } from "@react/shared/ui/Input"
import Label from "@react/shared/ui/Label"
import { addDays, formatShort, isoWeek, mondayOf } from "../lib/dates"

const weekLabel = monday => I18n.t("project_planning.copy_week.week_label", {
  number: isoWeek(monday), from: formatShort(monday), to: formatShort(addDays(monday, 6)),
})

const Choice = ({ active, onClick, children }) => (
  <div
    role="radio"
    aria-checked={active}
    tabIndex={0}
    onClick={onClick}
    onKeyDown={event => { if (event.key === " " || event.key === "Enter") onClick() }}
    className={clsx(
      "flex items-center gap-2.5 w-full px-3 py-2 rounded-lg border text-left text-[13px] cursor-pointer transition-colors",
      active ? "border-brand-500 bg-brand-50 text-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-300",
    )}
  >
    <span className={clsx("grid place-items-center w-4 h-4 rounded-full border", active ? "border-brand-500" : "border-slate-300")}>
      {active && <span className="w-2 h-2 rounded-full bg-brand-500" />}
    </span>
    {children}
  </div>
)

const CopyWeekModal = ({ isOpen, onClose, days, assignments, onConfirm }) => {
  const weeks = useMemo(() => [...new Set(days.map(day => mondayOf(day.date)))], [days])
  const [source, setSource] = useState(weeks[0])
  const [mode, setMode]     = useState("next")
  const [other, setOther]   = useState(addDays(weeks[0], 14))
  const [saving, setSaving] = useState(false)

  const target = mode === "next" ? addDays(source, 7) : mondayOf(other || source)
  const count  = assignments.filter(item => item.date >= source && item.date <= addDays(source, 6)).length
  const valid  = count > 0 && target !== source

  const confirm = () => {
    setSaving(true)
    onConfirm(source, target).finally(() => { setSaving(false); onClose() })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={I18n.t("project_planning.copy_week.title")}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{I18n.t("project_planning.actions.cancel")}</Button>
          <Button variant="primary" disabled={!valid || saving} onClick={confirm}>
            {saving && <FontAwesomeIcon icon="circle-notch" spin className="text-[11px]" />}
            {I18n.t("project_planning.copy_week.confirm", { count })}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-1.5">
        <Label size="xs" tone="default">{I18n.t("project_planning.copy_week.source")}</Label>
        <select className={inputVariants()} value={source} onChange={event => setSource(event.target.value)}>
          {weeks.map(monday => <option key={monday} value={monday}>{weekLabel(monday)}</option>)}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label size="xs" tone="default">{I18n.t("project_planning.copy_week.target")}</Label>
        <Choice active={mode === "next"} onClick={() => setMode("next")}>
          <span>{I18n.t("project_planning.copy_week.next")}</span>
          <span className="ml-auto text-[12px] text-slate-500">{weekLabel(addDays(source, 7))}</span>
        </Choice>
        <Choice active={mode === "other"} onClick={() => setMode("other")}>
          <span>{I18n.t("project_planning.copy_week.other")}</span>
          <input
            type="date"
            className={clsx(inputVariants({ size: "sm" }), "ml-auto w-[160px]")}
            value={other}
            onClick={event => { event.stopPropagation(); setMode("other") }}
            onChange={event => setOther(event.target.value)}
          />
        </Choice>
      </div>

      <div className={clsx("flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-[12.5px]", count ? "bg-slate-50 text-slate-600" : "bg-amber-50 text-amber-700")}>
        <FontAwesomeIcon icon={count ? "circle-info" : "triangle-exclamation"} className="mt-0.5 text-[12px]" />
        <span>
          {count
            ? I18n.t("project_planning.copy_week.summary", { count, target: weekLabel(target) })
            : I18n.t("project_planning.copy_week.nothing")}
        </span>
      </div>
    </Modal>
  )
}

export default CopyWeekModal
